import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SUPPORT_EMAIL, PRIVACY_EMAIL, COMPANY_NAME, DBA_NAME, WEBSITE_URL } from '../constants/contact'

// NOTE: Template Privacy Policy aligned with U.S. consumer-privacy expectations (CCPA/CPRA) and
// COPPA. NOT legal advice. Have a US-licensed attorney review before publication. Update the
// data-broker / sale-of-personal-information disclosures if business practices change.

export default function Privacy() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            {DBA_NAME}
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> May 19, 2026<br />
              <strong>Last Updated:</strong> May 19, 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                {COMPANY_NAME}, doing business as {DBA_NAME} (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or
                &quot;our&quot;), operates the website {WEBSITE_URL} and related services (collectively, the
                &quot;Service&quot;). This Privacy Policy explains what personal information we collect, how we use
                and share it, and what rights you have. By using the Service you agree to this Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Account information: full name, email address, password (stored as a salted hash).</li>
                <li>Vehicle information: license-plate number (optional, used to personalize parking sessions).</li>
                <li>Payment information: payment-card token and last four digits — full card numbers are handled by our PCI-compliant payment processor and are never stored on our servers.</li>
                <li>Support communications: emails and messages you send us.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Location data when you use the parking-search features (with your permission).</li>
                <li>Device and browser information: user-agent, operating system, screen size, language.</li>
                <li>Usage information: pages visited, features used, timestamps, referring URLs.</li>
                <li>IP address (used for security, analytics, and approximate geolocation).</li>
                <li>Cookies and similar technologies — see Section 6.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Information from Third Parties</h3>
              <p className="text-gray-700 mb-4">
                If you sign in with Google, we receive your name and email address from Google. We also receive
                transaction status and chargeback data from our payment processor, and aggregated analytics data
                from analytics providers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>To provide, operate, and improve the Service.</li>
                <li>To process subscriptions and payments, and to send billing communications.</li>
                <li>To authenticate users and secure accounts.</li>
                <li>To provide customer support and respond to inquiries.</li>
                <li>To send transactional emails (welcome, payment confirmation, cancellation, etc.).</li>
                <li>To detect, prevent, and address fraud, abuse, and security incidents.</li>
                <li>To comply with legal obligations and enforce our agreements.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Share Information</h2>
              <p className="text-gray-700 mb-4">
                We do <strong>not</strong> sell your personal information for monetary consideration. We share
                personal information only with the following categories of recipients:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Service providers:</strong> hosting, database, payment processing, email delivery, analytics, and customer-support tools, who are contractually bound to confidentiality and limited use.</li>
                <li><strong>Legal and safety:</strong> when required by law, valid subpoena, court order, or to protect the rights, property, or safety of users or third parties.</li>
                <li><strong>Business transfers:</strong> in connection with a merger, acquisition, financing, or sale of assets, subject to standard confidentiality obligations.</li>
                <li><strong>With your consent:</strong> for any other purpose disclosed at the time of collection.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain personal information for as long as your account is active and for a reasonable period
                thereafter to meet legal, accounting, tax, and fraud-prevention obligations (typically up to 7
                years for billing records). Aggregated or de-identified data may be retained indefinitely.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies and Similar Technologies</h2>
              <p className="text-gray-700 mb-4">
                We and our third-party providers use cookies, local storage, and similar technologies to
                authenticate users, remember preferences, measure traffic, and improve the Service. You can
                control cookies through your browser settings; disabling cookies may impair functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your California Privacy Rights (CCPA / CPRA)</h2>
              <p className="text-gray-700 mb-4">
                If you are a California resident, you have the following rights under the California Consumer
                Privacy Act (as amended by the CPRA):
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Right to know</strong> what personal information we collect, use, and share about you.</li>
                <li><strong>Right to delete</strong> personal information we hold about you, subject to legal exceptions.</li>
                <li><strong>Right to correct</strong> inaccurate personal information.</li>
                <li><strong>Right to opt out of sale or sharing</strong> — we do not sell personal information, and we do not share personal information for cross-context behavioral advertising.</li>
                <li><strong>Right to limit use of sensitive personal information</strong>, if applicable.</li>
                <li><strong>Right to non-discrimination</strong> for exercising your privacy rights.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, email{' '}
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-paybyphone-green underline">{PRIVACY_EMAIL}</a> from
                the email address associated with your account. We will verify your request and respond within
                45 days, as required by law. You may also designate an authorized agent to submit requests on
                your behalf, subject to verification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children&apos;s Privacy (COPPA)</h2>
              <p className="text-gray-700 mb-4">
                The Service is not directed to children under 13, and we do not knowingly collect personal
                information from children under 13. If you believe a child has provided us personal information,
                please contact{' '}
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-paybyphone-green underline">{PRIVACY_EMAIL}</a> and
                we will promptly delete it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Security</h2>
              <p className="text-gray-700 mb-4">
                We use industry-standard administrative, technical, and physical safeguards to protect your
                personal information, including encryption in transit (TLS), encryption at rest where supported,
                role-based access control, audit logging, and PCI-DSS-compliant payment-card handling by our
                processor. No method of transmission or storage is 100% secure, and we cannot guarantee absolute
                security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Users</h2>
              <p className="text-gray-700 mb-4">
                The Service is hosted in the United States. If you access the Service from outside the U.S.,
                your information will be transferred to and processed in the U.S. By using the Service, you
                consent to that transfer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Do Not Track</h2>
              <p className="text-gray-700 mb-4">
                Some browsers offer a &quot;Do Not Track&quot; (DNT) signal. Because no consistent industry standard
                exists for honoring DNT signals, we do not respond to them. We honor opt-out requests submitted
                through the mechanisms described in Section 7.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Policy from time to time. Material changes will be posted on this page with
                an updated &quot;Last Updated&quot; date, and, where appropriate, we will provide notice by email or
                in-app notification before the changes take effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                Questions or privacy requests:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Privacy:</strong> <a href={`mailto:${PRIVACY_EMAIL}`} className="text-paybyphone-green underline">{PRIVACY_EMAIL}</a></li>
                <li><strong>General support:</strong> <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a></li>
                <li><strong>Mailing address:</strong> see our <Link to="/contact" className="text-paybyphone-green underline">Contact page</Link>.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
